import type { InvoiceWizardData, InvoiceItem } from '../types/invoiceWizard.types';
import { round2, formatPriceDecimals } from '@/utils/format';
import { unescapeXml } from '@/utils/xmlEscape';
import { formatPhoneDGI } from '@/lib/utils/phone';
import { cufeConPrefijo, cufeSinPrefijo } from '../utils/cufe';


/**
 * Mapea el formato de pago del wizard al formato de la API
 * Ahora el wizard ya guarda el código correcto (01, 02, 03, etc.)
 */
const mapPaymentMethod = (paymentMethod: string): string => {
  // El wizard ya guarda el código correcto, solo lo retornamos
  return paymentMethod;
};

/**
 * Formatea una fecha al formato requerido por la API
 * @param date - Fecha en formato YYYY-MM-DD
 * @returns Fecha en formato ISO con timezone: "2025-10-30T11:10:00-06:00"
 */
const formatDateForAPI = (date: string | null | undefined): string => {
  if (!date) return '';

  // Parsear la fecha en formato YYYY-MM-DD.
  //
  // Se recorta la hora primero porque no todas las fechas llegan como
  // 'YYYY-MM-DD': la del documento referenciado viene con hora, sea del campo
  // datetime-local ('2026-07-30T14:35') o de la fecha de la factura original en
  // ISO. Partiendo por '-' sobre el texto completo, el día quedaba '30T14:35',
  // Number() lo convertía en NaN, y el payload viajaba con
  // 'dFechaDFRef': '2026-07-NaNT...'.
  const soloFecha = date.split('T')[0];
  const [year, month, day] = soloFecha.split('-').map(Number);

  // Una fecha inválida se devuelve vacía en vez de armar un texto con NaN: así
  // la validación la marca como faltante en lugar de enviarla malformada.
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    console.warn('⚠️ formatDateForAPI: fecha no reconocida, se omite:', date);
    return '';
  }

  // Obtener hora actual en Panamá (UTC-5)
  const now = new Date();
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  const panamaTime = new Date(utcMs - 5 * 3600000);

  const hours = panamaTime.getHours().toString().padStart(2, '0');
  const minutes = panamaTime.getMinutes().toString().padStart(2, '0');
  const seconds = panamaTime.getSeconds().toString().padStart(2, '0');

  const formattedYear = year;
  const formattedMonth = month.toString().padStart(2, '0');
  const formattedDay = day.toString().padStart(2, '0');

  // Panamá siempre es UTC-5, no tiene horario de verano
  return `${formattedYear}-${formattedMonth}-${formattedDay}T${hours}:${minutes}:${seconds}-05:00`;
};

/**
 * Formatea un número a string con 2 decimales de forma segura
 * Previene el error "e.toFixed is not a function" cuando el valor es string, null, undefined o NaN
 */
const formatAmount = (amount: unknown): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  if (isNaN(num) || amount === null || amount === undefined) {
    return '0.00';
  }
  // Usar round2() para compensar errores de punto flotante antes de formatear
  return round2(num).toFixed(2);
};

/**
 * Formatea coordenadas (latitud, longitud) a 6 decimales
 */
const formatCoordinates = (latitude: string, longitude: string): string => {
  const lat = parseFloat(latitude).toFixed(6);
  const lng = parseFloat(longitude).toFixed(6);
  return `${lat},${lng}`;
};

/**
 * Formatea el número de documento fiscal (dNroDF) con ceros a la izquierda
 * La DGI requiere formato de 10 dígitos
 * Ejemplo: "267" -> "0000000267"
 */
const formatInvoiceNumber = (invoiceNumber: string | null | undefined): string | null => {
  if (!invoiceNumber) return null;

  // Limpiar espacios y caracteres no numéricos
  const cleanNumber = invoiceNumber.toString().trim().replace(/\D/g, '');

  if (!cleanNumber) return null;

  // Pad con ceros a la izquierda para tener 10 dígitos
  return cleanNumber.padStart(10, '0');
};

const formatPhoneNumber = formatPhoneDGI;

/**
 * Normalizes text by removing accents and diacritics.
 * Skips normalization for comarcas indígenas (e.g. "COMARCA NGÄBE-BUGLÉ")
 * because the DGI requires their original diacritics.
 */
const normalizeAccents = (text: string): string => {
  if (!text) return text;

  if (text.toUpperCase().includes('COMARCA')) return text;

  // Preservar ñ/Ñ: el catálogo DGI (tabla 24) las conserva.
  // NFD descompone ñ → n + U+0303, por lo que el replace posterior las borraría.
  return text
    .replace(/ñ/g, '\u0001')
    .replace(/Ñ/g, '\u0002')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\u0001/g, 'ñ')
    .replace(/\u0002/g, 'Ñ');
};

/**
 * Mapea la tasa de ITBMS (%) al código requerido por la API
 *
 * Códigos válidos:
 * - 0%  -> "00"
 * - 7%  -> "01"
 * - 10% -> "02"
 * - 15% -> "03"
 *
 * @param taxRate - Tasa de ITBMS en porcentaje (0, 7, 10, 15)
 * @returns Código de tasa de ITBMS
 */
const mapITBMSRate = (taxRate: number): string => {
  // DGI C401: Valores válidos: 00=0%, 01=7%, 02=10%, 03=15%
  const rateMap: Record<number, string> = {
    0: '00',
    7: '01',
    10: '02',
    15: '03',
  };

  return rateMap[taxRate] || '01'; // Default a "01" (7%) si no se encuentra
};

/**
 * Mapea el código de ITBMS a la tasa porcentual
 * Función inversa de mapITBMSRate
 */
const unmapITBMSRate = (taxCode: string): number => {
  // DGI C401: Valores válidos: 00=0%, 01=7%, 02=10%, 03=15%
  const codeMap: Record<string, number> = {
    '00': 0,
    '01': 7,
    '02': 10,
    '03': 15,
  };

  return codeMap[taxCode] ?? 7; // Default a 7% si no se encuentra (usar ?? para que 0% no se convierta en 7%)
};

/**
 * Mapea un item del wizard al formato de la API
 */
const mapItem = (item: InvoiceItem, index: number) => {
  const secItem = (index + 1).toString().padStart(3, '0');

  return {
    dSecItem: secItem,
    // DGI C03: A 2-500 chars, Ocu 1-1
    dDescProd: (item.description ? `${item.productName} - ${item.description}` : item.productName).substring(0, 500),
    // DGI C04: A 1-20 chars, Ocu 0-1
    dCodProd: item.productCode?.trim() ? item.productCode.substring(0, 20) : null,
    // DGI C05: A 1-20 chars, Ocu 0-1
    cUnidad: item.unit || 'und',
    // DGI C06: N 1-11 with 0-6 decimals, Ocu 1-1
    dCantCodInt: item.quantity.toString(),
    dFechaFab: null,
    dFechaCad: null,
    // DGI C09 dCodCPBSabr: 2 dígitos (Segmento CPBS, Tabla 28), Ocu 0-1
    dCodCPBSabr: item.segment || null,
    // DGI C10 dCodCPBScmp: 4 dígitos (Segmento+Familia CPBS, Tabla 28), Ocu 0-1
    dCodCPBScmp: item.family || null,
    cUnidadCPBS: null,
    dInfEmFE: null,
    gPrecios: {
      // DGI C201: N 1-9p2-6, Ocu 1-1
      dPrUnit: formatPriceDecimals(item.unitPrice),
      // DGI C202: N 1-9p2-6, Ocu 0-1 — NO enviar si descuento=0 (evita error 2052 cuando C201=0)
      dPrUnitDesc: item.unitDiscount > 0 ? formatPriceDecimals(item.unitDiscount) : null,
      // DGI C203: N 1-9p2-6, Ocu 1-1 — C06*(C201-C202)
      dPrItem: formatPriceDecimals(item.subtotal),
      // DGI C204: N 1-9p2, Ocu 0-1 — Aludra requiere decimal, no acepta null
      dPrAcarItem: formatAmount(item.shippingAmount || 0),
      // DGI C205: N 1-9p2, Ocu 0-1 — Aludra requiere decimal, no acepta null
      dPrSegItem: formatAmount(item.insuranceAmount || 0),
      // DGI C206: N 1-9p2-6, Ocu 1-1 — C203+C204+C205+C402+C502+C602
      dValTotItem: formatPriceDecimals(item.total)
    },
    gCodItem: null,
    gITBMSItem: {
      dTasaITBMS: mapITBMSRate(item.taxRate),
      dValITBMS: formatPriceDecimals(item.taxAmount)    // DGI: 1-9p2-6
    },
    gISCItem: item.iscAmount > 0 ? {
      dTasaISC: item.iscRate.toString(),
      dValISC: formatPriceDecimals(item.iscAmount)      // DGI: 1-9p2-6
    } : null,
    gOTIItem: (item.gOTIItem && item.gOTIItem.length > 0) ? item.gOTIItem : null,
    // DGI: dNroPed es obligatorio si gPedComIr existe. No enviar sección sin número de pedido.
    // DGI E15: dNroPed=N(1-9), dSecItemPed=N(1-3), dInfEmPedIt=A(0-5000)
    gPedComIr: item.orderNumber ? {
      dNroPed: item.orderNumber.replace(/[^0-9]/g, '').substring(0, 9),
      dSecItemPed: item.orderItemSequence ? item.orderItemSequence.replace(/[^0-9]/g, '').substring(0, 3) : null,
      dInfEmPedIt: item.orderItemInfo ? item.orderItemInfo.substring(0, 5000) : null
    } : null,
    gMedicina: null
  };
};

/**
 * Mapea el objeto completo del wizard al formato de la API de Aludra
 */
export const mapInvoiceToAPIFormat = (wizardData: InvoiceWizardData) => {
  console.log('🔄 Iniciando mapeo de factura al formato de API...');

  // Referencia al documento original (Notas de Crédito/Débito).
  //
  // Va DENTRO de `dGen`, no al nivel del payload: así lo define el modelo de la
  // API (`DGen.GDFRef` en AludraPortalEInvoice) y así lo llena su propio portal.
  // Enviándolo al nivel de arriba, la API nunca lo lee y la nota se emite sin
  // referencia — se guarda como `gDFRef: null` aunque el portal muestre la
  // referencia en pantalla.
  //
  // El CUFE viaja con el prefijo `FE` y los campos vacíos como `null`, igual que
  // en el portal de Aludra: un string vacío no es "sin referencia" para la API.
  const referenciaDocumento = wizardData.gDFRef
    ? {
        dNroFacIE: wizardData.gDFRef.dNroFacIE?.trim() || null,
        dNroFacPap: wizardData.gDFRef.dNroFacPap?.trim() || null,
        dCufeRef: cufeConPrefijo(wizardData.gDFRef.dCufeRef) || null,
        dFechaDFRef: formatDateForAPI(wizardData.gDFRef.dFechaDFRef) || null,
      }
    : null;

  // Verificar si es modo corrección con mismo CUFE
  const isFixingWithSameCufe = wizardData.isFixingEBill === true;
  if (isFixingWithSameCufe) {
    console.log('🔧 Modo corrección con mismo CUFE detectado');
    console.log('📋 Datos originales:', {
      cufe: wizardData.originalCufe,
      seg: wizardData.originalSeg,
      invoiceNumber: wizardData.originalInvoiceNumber,
      billingPointCode: wizardData.originalBillingPointCode,
      branchOfficeCode: wizardData.originalBranchOfficeCode,
      emissionDate: wizardData.originalEmissionDate,
    });
  }

  const mappedData = {
    Data: null,
    info: null,
    dVerForm: "1.00",
    // DGI: dNroPed es obligatorio si gPedComGl existe. No enviar sección sin número de pedido.
    // DGI F10: dNroPed=A(1-12), dNumAcept=A(1-12), dCodRec=A(1-50), dCodSisEm=A(1-50), dInfEmPedGl=A(0-5000)
    // Si el usuario solo puso notas (sin pedido), auto-generar número de pedido para cumplir con DGI
    gPedComGl: (() => {
      const hasOrderNumber = !!wizardData.globalOrderNumber;
      const hasNotes = !!wizardData.notes?.trim();
      const hasOrderInfo = !!wizardData.globalOrderInfo;

      if (!hasOrderNumber && !hasNotes && !hasOrderInfo) return null;

      return {
        dNroPed: hasOrderNumber
          ? wizardData.globalOrderNumber!.substring(0, 12)
          : '1', // DGI requiere dNroPed si gPedComGl existe
        dNumAcept: wizardData.globalOrderAcceptanceNumber ? wizardData.globalOrderAcceptanceNumber.substring(0, 12) : null,
        dCodRec: wizardData.globalOrderReceiverCode ? wizardData.globalOrderReceiverCode.substring(0, 50) : null,
        dCodSisEm: wizardData.globalOrderIssuerSystemCode ? wizardData.globalOrderIssuerSystemCode.substring(0, 50) : null,
        dInfEmPedGl: (wizardData.notes?.trim() || wizardData.globalOrderInfo || '').substring(0, 5000) || null,
      };
    })(),
    dGen: {
      // Para corrección con mismo CUFE: usar CUFE original, sino null
      dId: isFixingWithSameCufe ? wizardData.originalCufe : null,
      dVerForm: 0,
      iAmb: parseInt(import.meta.env.VITE_IAMB || '2', 10), // 1=Producción, 2=Pruebas
      iTpEmis: "01", // Tipo de emisión
      dFechaCont: null,
      dMotCont: null,
      iDoc: wizardData.documentType,
      // Referencia al documento original: ver `referenciaDocumento` arriba.
      gDFRef: referenciaDocumento,
      // Para corrección con mismo CUFE: usar número original
      // Formatear con ceros a la izquierda (10 dígitos) para cumplir con DGI
      dNroDF: isFixingWithSameCufe
        ? formatInvoiceNumber(wizardData.originalInvoiceNumber)
        : (wizardData.invoiceNumberMode === 'manual' ? formatInvoiceNumber(wizardData.invoiceNumber) : null),
      // Para corrección con mismo CUFE: usar punto de facturación original
      // DGI B08: A 3 max
      dPtoFacDF: isFixingWithSameCufe
        ? (wizardData.originalBillingPointCode || '').substring(0, 3)
        : (wizardData.billingPointCode || '').substring(0, 3),
      // Para corrección con mismo CUFE: usar segmento original
      dSeg: isFixingWithSameCufe ? wizardData.originalSeg : null,
      // Para corrección con mismo CUFE: usar fecha de emisión original
      dFechaEm: isFixingWithSameCufe
        ? wizardData.originalEmissionDate
        : formatDateForAPI(wizardData.invoiceDate),
      // dFechaSalida debe usar fecha de emisión (DGI permite max 72h en futuro)
      // La fecha de vencimiento de pago se maneja en gPagPlazo, no aquí
      dFechaSalida: isFixingWithSameCufe
        ? wizardData.originalEmissionDate
        : formatDateForAPI(wizardData.invoiceDate),
      iNatOp: wizardData.operationNature,
      iTipoOp: wizardData.operationType,
      iDest: wizardData.operationDestination,
      iFormCAFE: wizardData.documentFormatGeneration,
      iEntCAFE: "1",
      dEnvFE: "1",
      iProGen: "2", // Proceso de generación
      // DGI B19: Ocu 0-1 — "Si no es venta, no informar este campo"
      // Solo informar cuando operationType='1' (Salida/Venta) y naturaleza es de venta (01, 02, 03, 04, 05, 10, 12, 13, 14)
      iTipoTranVenta: wizardData.operationType === '1' && !['20', '21', '11'].includes(wizardData.operationNature)
        ? (wizardData.salesTransactionType || "1")
        : null,
      gEmis: {
        // En ambiente de pruebas (iAmb=2), usar texto especial según normativa DGI
        dNombEm: import.meta.env.VITE_IAMB === '2'
          ? "FE generada en ambiente de pruebas - sin valor comercial ni fiscal"
          : wizardData.issuerName,
        // Para corrección con mismo CUFE: usar sucursal original
        // DGI B301: A 4 max — truncar para evitar rechazo
        dSucEm: isFixingWithSameCufe
          ? (wizardData.originalBranchOfficeCode || '').substring(0, 4)
          : (wizardData.branchOfficeAddressCode || wizardData.branchOfficeId || '').substring(0, 4),
        dCoordEm: wizardData.branchOfficeLatitude && wizardData.branchOfficeLongitude
          ? formatCoordinates(wizardData.branchOfficeLatitude, wizardData.branchOfficeLongitude)
          : null,
        dDirecEm: wizardData.branchOfficeAddress,
        gRucEmi: {
          dTipoRuc: wizardData.issuerTypeTaxpayer || "2", // Tipo de RUC del emisor (1=Natural, 2=Jurídico)
          dRuc: wizardData.issuerCode?.trim(),
          dDV: (wizardData.issuerDv?.trim() || '').padStart(2, '0')
        },
        gUbiEm: {
          dCodUbi: wizardData.branchOfficeFiscalCode,
          dCorreg: normalizeAccents(wizardData.branchOfficeTownship),
          dDistr: normalizeAccents(wizardData.branchOfficeDistrict),
          dProv: normalizeAccents(wizardData.branchOfficeProvince)
        },
        dTfnEm: formatPhoneNumber(wizardData.issuerPhone),
        dCorElectEm: wizardData.issuerEmail || null
      },
      gDatRec: {
        iTipoRec: wizardData.receiverType,
        // DGI B403: A 2-150, Ocu 0-1 — requerido para 01/03, opcional para 02/04
        // Enviar null si está vacío para evitar rechazo por min 2 chars
        dNombRec: wizardData.customerName?.trim()
          ? wizardData.customerName.substring(0, 150)
          : null,
        dSucRec: wizardData.receiverType === '04'
          ? null // For foreign customers, dSucRec is null
          : (wizardData.customerAddress
            ? `${wizardData.customerAddress}, ${wizardData.customerCountry}, ${wizardData.customerDistrict}`
            : null),
        dCoordRec: null,
        // DGI B404: A 1-100, Ocu 0-1 — requerido para 01/03, opcional para 02/04
        dDirecRec: wizardData.customerAddress?.trim()
          ? wizardData.customerAddress.substring(0, 100)
          : null,
        // DGI 1611: Si iDest=1 (Panamá), cPaisRec DEBE ser PA para evitar warning
        // El país real del extranjero queda en gIdExt.dPaisExt
        // Solo cuando iDest=2 (Extranjero), cPaisRec usa el país del cliente extranjero
        cPaisRec: wizardData.receiverType === '04'
          ? (wizardData.operationDestination === '2' ? (wizardData.customerForeignCountry || '') : 'PA')
          : (wizardData.branchOfficeCountryCode || 'PA'),
        // dPaisRecDesc: solo cuando cPaisRec es "ZZ" (país no está en tabla DGI)
        dPaisRecDesc: (() => {
          const countryCode = wizardData.receiverType === '04'
            ? (wizardData.operationDestination === '2' ? (wizardData.customerForeignCountry || '') : 'PA')
            : (wizardData.branchOfficeCountryCode || 'PA');
          return countryCode === 'ZZ' ? (wizardData.customerCountry?.trim() || null) : null;
        })(),
        // DGI B402: gRucRec
        // - REQUERIDO si B401=01 (Contribuyente) o B401=03 (Gobierno)
        // - OPCIONAL si B401=02 (Consumidor Final) — solo si el cliente proporcionó RUC/cédula
        // - PROHIBIDO si B401=04 (Extranjero) — código 1619
        gRucRec: (() => {
          if (wizardData.receiverType === '04') return null; // Extranjero: prohibido
          if (wizardData.receiverType === '02') {
            // Consumidor Final (DGI B402/B4022): puede llenarse con RUC o cédula si el cliente lo solicita
            // Pasaporte (03) y Otro (04) NO son válidos para dRuc
            const ruc = wizardData.customerRuc?.trim();
            if (!ruc || wizardData.customerRucType === '03' || wizardData.customerRucType === '04') return null;
            return {
              dTipoRuc: wizardData.customerRecepType,
              dRuc: ruc,
              dDV: (wizardData.customerDv?.trim() || '00').padStart(2, '0')
            };
          }
          // Contribuyente (01) y Gobierno (03): requerido
          return {
            dTipoRuc: wizardData.customerRecepType,
            dRuc: wizardData.customerRuc?.trim() || '',
            dDV: (wizardData.customerDv?.trim() || '').padStart(2, '0')
          };
        })(),
        // DGI B405: gUbiRec (Provincia, Distrito, Corregimiento)
        // - REQUERIDO si B401=01 (Contribuyente) o B401=03 (Gobierno)
        // - PROHIBIDO si B401=02 (Consumidor Final) o B401=04 (Extranjero)
        gUbiRec: (wizardData.receiverType === '01' || wizardData.receiverType === '03') ? {
          dCodUbi: wizardData.customerAddressFiscalCode,
          dCorreg: normalizeAccents(wizardData.customerTownship),
          dDistr: normalizeAccents(wizardData.customerDistrict),
          dProv: normalizeAccents(wizardData.customerProvince)
        } : null,
        // DGI B408: A 7-16, Ocu 0-3 — siempre opcional
        dTfnRec: wizardData.customerPhone?.trim() ? formatPhoneNumber(wizardData.customerPhone) : null,
        // DGI B409: A 50, Ocu 0-3 — siempre opcional
        dCorElectRec: wizardData.customerEmail?.trim() || null,
        // gIdExt solo se usa para clientes extranjeros (iTipoRec == "04")
        // Usa customerRuc para dIdExt y customerForeignCountry (código ISO) para dPaisExt
        gIdExt: wizardData.receiverType === '04' ? {
          dIdExt: wizardData.customerRuc?.trim(),
          dPaisExt: wizardData.customerForeignCountry?.trim() || wizardData.customerCountry?.trim()
        } : null
      },
      // gAutXml - Autorización XML del receptor
      // Requerido para Contribuyente (01) y Gobierno (03), null para los demás
      gAutXml: (wizardData.receiverType === '01' || wizardData.receiverType === '03') ? {
        gRucAutXml: {
          dTipoRuc: wizardData.customerRecepType,
          dRuc: wizardData.customerRuc?.trim() || '',
          dDv: (wizardData.customerDv?.trim() || '').padStart(2, '0')
        }
      } : null,
      // gFExp - Datos de exportación (cuando iDest=2)
      // Para tipo 03 (Exportación): siempre enviar gFExp
      // Para otros tipos con iDest=2: solo enviar si el usuario llenó Incoterms
      gFExp: (wizardData.operationDestination === '2' && (wizardData.documentType === '03' || wizardData.exportIncoterms)) ? (() => {
        // DGI requiere cMoneda cuando se envía gFExp — defaultear a USD si no se especifica
        const currency = wizardData.exportCurrency || 'USD';
        const exchangeRate = wizardData.exportExchangeRate || 1;
        const port = wizardData.exportPortOfEmbarkation?.trim();
        // Aludra siempre serializa dPuertoEmbarq en el XML cuando gFExp existe,
        // incluso si no lo enviamos. DGI requiere MinLength >= 5 si el campo está presente.
        // Por eso siempre enviamos un valor válido.
        const validPort = (port && port.length >= 5) ? port : 'No Aplica';
        return {
          ...(wizardData.exportIncoterms ? { cCondEntr: wizardData.exportIncoterms } : {}),
          cMoneda: currency,
          ...(currency === 'ZZZ' && wizardData.exportCurrencyDescription ? {
            cMonedaDesc: wizardData.exportCurrencyDescription
          } : {}),
          dCambio: formatAmount(exchangeRate),
          dVTotEst: formatAmount(wizardData.grandTotal * exchangeRate),
          dPuertoEmbarq: validPort,
        };
      })() : null
    },
    noDocumentPrint: null,
    xFe: null,
    gItem: wizardData.items.map((item, index) => mapItem(item, index)),
    gTot: {
      dTotNeto: formatAmount(wizardData.subtotal),
      dTotITBMS: formatAmount(wizardData.totalTax),
      dTotISC: formatAmount(wizardData.totalIsc),
      // dTotGravado = suma de ITBMS + ISC + todos los OTI
      dTotGravado: formatAmount(
        wizardData.totalTax +
        wizardData.totalIsc +
        (wizardData.gOTITotal && wizardData.gOTITotal.length > 0
          ? wizardData.gOTITotal.reduce((sum, oti) => sum + parseFloat(oti.dValOTITotal), 0)
          : 0)
      ),
      // dTotDesc: Cuando los descuentos son unitarios (a nivel de item), debe ser "0.00"
      // Solo se usa para descuentos globales aplicados a toda la factura
      dTotDesc: wizardData.globalDiscounts && wizardData.globalDiscounts.length > 0
        ? formatAmount(wizardData.globalDiscounts.reduce((sum, disc) => sum + disc.amount, 0))
        : "0.00",
      dTotAcar: formatAmount(wizardData.shippingCost),
      dTotSeg: formatAmount(wizardData.insuranceCost),
      dVTot: formatAmount(wizardData.grandTotal),
      dTotRec: (() => {
        // Para Crédito (2): el total recibido es el monto total (a crédito)
        if (wizardData.paymentCondition === '2') {
          return formatAmount(wizardData.grandTotal);
        }
        // Para Contado (1) o Mixto (3): suma de pagos
        if (wizardData.payments && wizardData.payments.length > 0) {
          return formatAmount(wizardData.payments.reduce((sum, p) => sum + p.amount, 0));
        }
        return formatAmount(wizardData.amountPaid);
      })(),
      dVuelto: (() => {
        // Para Crédito (2) o Mixto (3): no hay vuelto
        if (wizardData.paymentCondition === '2' || wizardData.paymentCondition === '3') {
          return "0.00";
        }
        // Solo para Contado (1): calcular vuelto si aplica
        const totalPaid = wizardData.payments && wizardData.payments.length > 0
          ? wizardData.payments.reduce((sum, p) => sum + p.amount, 0)
          : wizardData.amountPaid;
        const change = totalPaid - wizardData.grandTotal;
        return formatAmount(change > 0 ? change : 0);
      })(),
      iPzPag: wizardData.paymentCondition || "1", // Tiempo de pago: 1=Contado, 2=Crédito/Plazo, 3=Mixto
      dNroItems: wizardData.items.length,
      dVTotItems: formatAmount(wizardData.items.reduce((sum, item) => sum + item.total, 0)),
      // gDescBonif: Solo se crea para descuentos globales, NO para descuentos unitarios
      // Es un array de objetos con dDetalDesc y dValDesc
      gDescBonif: wizardData.globalDiscounts && wizardData.globalDiscounts.length > 0
        ? wizardData.globalDiscounts.map(discount => ({
          dDetalDesc: discount.description,
          dValDesc: formatAmount(discount.amount)
        }))
        : null,
      gFormaPago: (() => {
        // Para Crédito (2): forma de pago es "01" (Crédito) con el monto total
        if (wizardData.paymentCondition === '2') {
          return [
            {
              iFormaPago: "01", // 01 = Crédito
              dFormaPagoDesc: null,
              PagoDesc: null,
              dVlrCuota: formatAmount(wizardData.grandTotal)
            }
          ];
        }
        // Para Contado (1) o Mixto (3): usar los pagos registrados
        if (wizardData.payments && wizardData.payments.length > 0) {
          return wizardData.payments.map(payment => ({
            iFormaPago: payment.method,
            // DGI D302a (2602): dFormaPagoDesc solo para método 99 (Otro)
            dFormaPagoDesc: payment.method === '99' ? (payment.description || null) : null,
            PagoDesc: payment.method === '99' ? (payment.description || null) : null,
            dVlrCuota: formatAmount(payment.amount)
          }));
        }
        // Fallback para Contado sin pagos registrados
        return [
          {
            iFormaPago: mapPaymentMethod(wizardData.paymentMethod),
            dFormaPagoDesc: null,
            PagoDesc: null,
            dVlrCuota: formatAmount(wizardData.amountPaid)
          }
        ];
      })(),
      gOTITotal: (wizardData.gOTITotal && wizardData.gOTITotal.length > 0) ? wizardData.gOTITotal : null,
      gRetenc: wizardData.retention !== '0' ? {
        cCodRetenc: wizardData.retention,
        cValRetenc: (() => {
          // Calcular el valor de retención: porcentaje × ITBMS
          // 1, 3 = 100% | 2, 4, 7 = 50% | 8 = monto manual (variable)
          if (wizardData.retention === '8') {
            return formatAmount(wizardData.retentionAmount || 0);
          }
          const retentionPercentages: Record<string, number> = {
            '1': 1.00,    // 100% - Servicio profesional al estado
            '2': 0.50,    // 50% - Venta bienes/servicios al estado
            '3': 1.00,    // 100% - No domiciliado
            '4': 0.50,    // 50% - Compra bienes/servicios
            '7': 0.50,    // 50% - Comercio afiliado TC/TD
          };
          const percentage = retentionPercentages[wizardData.retention] || 0;
          const retentionValue = wizardData.totalTax * percentage; // totalTax = ITBMS
          return formatAmount(retentionValue);
        })()
      } : null,
      // gPagPlazo: Solo se incluye cuando paymentCondition != "1" (no es Contado)
      gPagPlazo: (wizardData.paymentCondition !== '1' && wizardData.paymentTerms && wizardData.paymentTerms.length > 0)
        ? wizardData.paymentTerms
        : null,
      gRucEmi: null,
      gUbiEm: null,
      dTfnEm: null,
      dCorElectEmi: null
    },
    gInfoLog: null,
    gLcEntr: null,
    gExtra: {
      isOnFixingEBill: wizardData.isFixingEBill || false,
      gCompanyCode: wizardData.issuerCompanyCode, // Company Code (ej: Hype040124)
      isValidationsOn: true,
      gNotification: {
        dAttachments: {
          dResponseAuthXML: true,
          dResponseXML: true,
          dCFe: true,
          dCAFE: true
        },
        dChannels: [
          {
            dChannelName: "Email",
            dReceivers: {
              dReceiversList: [
                {
                  Email: wizardData.customerEmail,
                  Name: wizardData.customerName
                }
              ]
            }
          }
        ]
      }
    }
  };

  console.log('✅ Mapeo completado');
  console.log('📦 Objeto mapeado:', JSON.stringify(mappedData, null, 2));

  return mappedData;
};

/**
 * Calcula días hábiles en el futuro (excluyendo sábados y domingos)
 * Retorna la cantidad de días hábiles entre today y targetDate (positivo = futuro)
 */
const countBusinessDaysBetween = (from: Date, to: Date): number => {
  let count = 0;
  const current = new Date(from);
  current.setDate(current.getDate() + 1); // Start counting from next day
  while (current <= to) {
    const dayOfWeek = current.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  return count;
};

/** Lista de Incoterms válidos según DGI B501 (código 1652) */
const VALID_INCOTERMS = ['CFR', 'CIF', 'CIP', 'CPT', 'DAP', 'DAT', 'DDP', 'EXW', 'FAS', 'FCA', 'FOB'];

/** Códigos de moneda ISO válidos para exportación DGI B502 (código 1653) */
const VALID_EXPORT_CURRENCIES = [
  'USD', 'PAB', 'EUR', 'GBP', 'JPY', 'CAD', 'MXN', 'COP', 'CRC', 'BRL',
  'ARS', 'CLP', 'PEN', 'UYU', 'BOB', 'PYG', 'VES', 'DOP', 'GTQ', 'HNL',
  'NIO', 'SVC', 'BZD', 'HTG', 'JMD', 'TTD', 'BBD', 'AWG', 'ANG', 'KYD',
  'BSD', 'BMD', 'XCD', 'GYD', 'SRD', 'FKP', 'CHF', 'SEK', 'NOK', 'DKK',
  'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'HRK', 'RUB', 'TRY', 'ILS', 'AED',
  'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'CNY', 'KRW', 'INR', 'THB', 'SGD',
  'MYR', 'IDR', 'PHP', 'VND', 'TWD', 'HKD', 'AUD', 'NZD', 'ZAR', 'EGP',
  'NGN', 'KES', 'GHS', 'TZS', 'UGX', 'MAD', 'XOF', 'XAF',
];

/** Códigos válidos de forma de pago DGI D301 (código 2600) */
const VALID_PAYMENT_METHODS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '99'];

/** Códigos válidos de retención DGI D401 (código 2650) */
const VALID_RETENTION_CODES = ['1', '2', '3', '4', '7', '8'];

/**
 * Valida que todos los campos requeridos estén presentes antes del mapeo
 * Incluye todas las validaciones DGI de Rechazo (R) según Ficha Técnica V1.00 Abril 2025
 */
export const validateInvoiceData = (wizardData: InvoiceWizardData): { valid: boolean; errors: string[]; warnings: string[] } => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // ═══════════════════════════════════════════
  // VALIDACIONES BÁSICAS DEL EMISOR
  // ═══════════════════════════════════════════
  if (!wizardData.issuerName) errors.push('El nombre del emisor es requerido');
  if (!wizardData.issuerCode) errors.push('El código/RUC del emisor es requerido');
  if (!wizardData.issuerCompanyCode) errors.push('El código de compañía del emisor es requerido');
  if (!wizardData.issuerDv) errors.push('El DV del emisor es requerido');
  if (!wizardData.branchOfficeId) errors.push('La sucursal es requerida');
  if (!wizardData.billingPointCode) errors.push('El punto de facturación es requerido');

  // DGI B08 (1515): Punto de facturación "000" no es válido → R
  if (wizardData.billingPointCode === '000') {
    errors.push('El punto de facturación no puede ser "000" (DGI B08 código 1515)');
  }

  // Validaciones de la sucursal
  if (!wizardData.branchOfficeAddress) errors.push('La dirección de la sucursal es requerida');
  if (!wizardData.branchOfficeFiscalCode) errors.push('El código fiscal de la sucursal es requerido');
  if (!wizardData.branchOfficeProvince) errors.push('La provincia de la sucursal es requerida');
  if (!wizardData.branchOfficeDistrict) errors.push('El distrito de la sucursal es requerido');
  if (!wizardData.branchOfficeTownship) errors.push('El corregimiento de la sucursal es requerido');
  // DGI B304: dCoordEm es obligatorio (Ocu 1-1) — coordenadas de la sucursal emisora
  if (!wizardData.branchOfficeLatitude || !wizardData.branchOfficeLongitude) {
    errors.push('Las coordenadas de la sucursal son requeridas. Verifique que la sucursal tenga ubicación configurada (DGI B304)');
  }

  // ═══════════════════════════════════════════
  // VALIDACIONES DEL RECEPTOR (condicionales según DGI B401)
  // ═══════════════════════════════════════════
  const isConsumidorFinal = wizardData.receiverType === '02';
  const isExtranjero = wizardData.receiverType === '04';
  const isContribuyente = wizardData.receiverType === '01';
  const isGobierno = wizardData.receiverType === '03';

  // DGI B403: Nombre requerido para 01/03, opcional para 02/04
  if ((isContribuyente || isGobierno) && !wizardData.customerName) {
    errors.push('Nombre del receptor obligatorio para Contribuyente/Gobierno (DGI B403)');
  }

  // DGI B409: Email opcional para DGI, requerido por portal para 01/03/04
  if (!isConsumidorFinal && !wizardData.customerEmail) {
    errors.push('El email del cliente es requerido para enviar la factura');
  }

  // DGI B404: Dirección requerida para 01/03, opcional para 02/04
  if ((isContribuyente || isGobierno) && !wizardData.customerAddress) {
    errors.push('Dirección del receptor obligatoria para Contribuyente/Gobierno (DGI B404)');
  }

  // DGI B405: Ubicación requerida para 01/03, prohibida para 02/04
  if ((isContribuyente || isGobierno) && !wizardData.customerProvince) {
    errors.push('Ubicación del receptor obligatoria para Contribuyente/Gobierno (DGI B405)');
  }

  // DGI B402: RUC/DV requerido para 01/03
  if ((isContribuyente || isGobierno) && !wizardData.customerRuc) {
    errors.push('El RUC del cliente es requerido para Contribuyente/Gobierno');
  }
  if ((isContribuyente || isGobierno) && !wizardData.customerDv) {
    errors.push('El DV del cliente es requerido para Contribuyente/Gobierno');
  }

  // DGI B402d (1621): Consumidor Final (02) con tipo RUC Jurídico (2) → R
  if (isConsumidorFinal && wizardData.customerRecepType === '2') {
    errors.push('Un Consumidor Final no puede tener tipo de RUC Jurídico (DGI B402d código 1621)');
  }

  // DGI B406: ID extranjero obligatorio cuando receptor tipo = 04
  if (isExtranjero) {
    if (!wizardData.customerRuc?.trim()) {
      errors.push('El ID del extranjero (dIdExt) es obligatorio para receptor tipo Extranjero (DGI B406)');
    }
    if (!wizardData.customerForeignCountry?.trim()) {
      errors.push('El país del extranjero (dPaisExt) es obligatorio para receptor tipo Extranjero (DGI B406)');
    }
  }

  // DGI B406a (1619): Extranjero no puede tener grupo RUC (B402) y grupo extranjero (B406) simultáneamente → R
  if (isExtranjero && wizardData.customerRuc?.trim() && wizardData.customerDv?.trim()) {
    // Si es extranjero y tiene DV, significa que se informó grupo RUC completo
    errors.push('Receptor Extranjero no puede tener grupo de RUC (con DV) y grupo de identificación de extranjero simultáneamente (DGI B406a código 1619)');
  }

  // B410b, B411, B401a, B401b: No validamos en frontend — DGI lo valida al recibir el XML
  // (Igual que el portal legacy AludraPortalEInvoice)

  // Validación de coordenadas dentro de Panamá (DGI B304, B304a)
  if (wizardData.branchOfficeLatitude && wizardData.branchOfficeLongitude) {
    const lat = parseFloat(wizardData.branchOfficeLatitude);
    const lng = parseFloat(wizardData.branchOfficeLongitude);
    if (!isNaN(lat) && (lat < 7 || lat > 9.5)) {
      errors.push('Latitud fuera de rango de Panamá (7° a 9.5°) - DGI B304 código 1568');
    }
    if (!isNaN(lng) && (lng < -83 || lng > -77)) {
      errors.push('Longitud fuera de rango de Panamá (-83° a -77°) - DGI B304a código 1569');
    }
  }

  // ═══════════════════════════════════════════
  // VALIDACIONES DE FECHA (B10, B10a, B11, B11a)
  // ═══════════════════════════════════════════
  if (!wizardData.invoiceDate) errors.push('La fecha de emisión es requerida');
  if (!wizardData.documentType) errors.push('El tipo de documento es requerido');

  if (wizardData.invoiceDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [year, month, day] = wizardData.invoiceDate.split('-').map(Number);
    const invoiceDate = new Date(year, month - 1, day);
    invoiceDate.setHours(0, 0, 0, 0);
    const diffMs = today.getTime() - invoiceDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // DGI B10 (1519): >30 días calendario atrás → Notificación (N), NO rechazo
    if (diffDays > 30) {
      warnings.push('La fecha de emisión es mayor a 30 días atrás. La DGI enviará una notificación (DGI B10 código 1519)');
    }

    // DGI B10a (1520): >2 días hábiles en futuro → Rechazo (R)
    if (diffDays < 0) {
      // La fecha es en el futuro
      const businessDaysAhead = countBusinessDaysBetween(today, invoiceDate);
      if (businessDaysAhead > 2) {
        errors.push('La fecha de emisión no puede ser mayor a 2 días hábiles en el futuro (DGI B10a código 1520)');
      }
    }

    // DGI B11 (1522): Fecha salida < fecha emisión → R (defensiva, en nuestro mapper son iguales)
    // DGI B11a (1523): Fecha salida > 5 días después de emisión → R (defensiva)
    // Nota: En nuestro mapper dFechaSalida = dFechaEm, pero validamos por si cambia en el futuro
  }

  // B14a, B14b: No validamos en frontend — DGI lo valida al recibir el XML
  // (Igual que el portal legacy AludraPortalEInvoice)

  // DGI B19 (1531): Tipo de transacción de venta debe ser 1, 2, 3 o 4 → R
  if (wizardData.salesTransactionType && !['1', '2', '3', '4'].includes(wizardData.salesTransactionType)) {
    errors.push(`Tipo de transacción de venta "${wizardData.salesTransactionType}" no es válido. Debe ser 1 (Giro del negocio), 2 (Activo fijo), 3 (Bienes raíces) o 4 (Prestación de servicio) (DGI B19 código 1531)`);
  }

  // ═══════════════════════════════════════════
  // EXPORTACIÓN (B50, B50a, B501, B502)
  // ═══════════════════════════════════════════

  // DGI B50a (1651): Incoterms obligatorio solo para Factura de Exportación (03)
  // Para otros tipos con destino extranjero, Incoterms es opcional
  if (wizardData.operationDestination === '2' && wizardData.documentType === '03') {
    if (!wizardData.exportIncoterms) {
      errors.push('Factura de Exportación requiere Incoterms (DGI B50a código 1651)');
    }
  }

  // DGI B501 (1652): Incoterms debe ser válido
  if (wizardData.exportIncoterms) {
    const incoterm = wizardData.exportIncoterms.toUpperCase().trim();
    if (!VALID_INCOTERMS.includes(incoterm)) {
      errors.push(`Incoterms "${wizardData.exportIncoterms}" no es válido. Valores aceptados: ${VALID_INCOTERMS.join(', ')} (DGI B501 código 1652)`);
    }
  }

  // DGI B502 (1653): Código de moneda ISO válido
  if (wizardData.exportCurrency) {
    const currency = wizardData.exportCurrency.toUpperCase().trim();
    if (!VALID_EXPORT_CURRENCIES.includes(currency)) {
      errors.push(`Moneda de exportación "${wizardData.exportCurrency}" no es un código ISO válido (DGI B502 código 1653)`);
    }
  }

  // DGI B504 (1656): Tipo de cambio obligatorio si existe B502 (moneda seleccionada)
  if (wizardData.operationDestination === '2' && wizardData.exportCurrency) {
    if (!wizardData.exportExchangeRate || parseFloat(String(wizardData.exportExchangeRate)) <= 0) {
      errors.push('Tipo de cambio obligatorio cuando se selecciona moneda de exportación (DGI B504 código 1656)');
    }
    // DGI B503 (1654): Descripción obligatoria si B502=ZZZ
    if (wizardData.exportCurrency === 'ZZZ' && !wizardData.exportCurrencyDescription?.trim()) {
      errors.push('Descripción de moneda obligatoria cuando la moneda es ZZZ (DGI B503 código 1654)');
    }
  }

  // DGI B505: Monto total en moneda extranjera - se calcula automáticamente en el mapper (grandTotal * exchangeRate)

  // ═══════════════════════════════════════════
  // NOTAS DE CRÉDITO/DÉBITO (B606b, B606c, E02-E04)
  // ═══════════════════════════════════════════

  // DGI E02-E04: Nota crédito/débito - fecha referenciada máximo 180 días
  if ((wizardData.documentType === '04' || wizardData.documentType === '05') && wizardData.gDFRef?.dFechaDFRef) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const refDate = new Date(wizardData.gDFRef.dFechaDFRef);
    refDate.setHours(0, 0, 0, 0);
    const diffMs = today.getTime() - refDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays > 180) {
      errors.push('La fecha del documento referenciado no puede ser mayor a 180 días atrás (DGI E02-E04)');
    }
  }

  // DGI B606b (1705): NC/ND referenciada (04, 05) DEBE tener gDFRef → R
  //
  // No alcanza con que el objeto exista: uno con todos los campos vacíos deja
  // pasar una nota tipo 04 sin referencia, y la DGI la aplica como genérica.
  if (wizardData.documentType === '04' || wizardData.documentType === '05') {
    const ref = wizardData.gDFRef;
    const identificaElDocumento = Boolean(
      ref && (ref.dCufeRef?.trim() || ref.dNroFacIE?.trim() || ref.dNroFacPap?.trim())
    );
    if (!identificaElDocumento) {
      errors.push('Nota de Crédito/Débito referenciada requiere documento de referencia (DGI B606b código 1705)');
    }
    if (identificaElDocumento && !ref?.dFechaDFRef) {
      errors.push('La referencia al documento original requiere su fecha de emisión (DGI B606b código 1705)');
    }
  }

  // DGI B606c (1706): NC/ND genérica (06, 07) NO debe tener dCufeRef → R
  if ((wizardData.documentType === '06' || wizardData.documentType === '07') && wizardData.gDFRef?.dCufeRef) {
    errors.push('Nota de Crédito/Débito genérica no debe incluir CUFE de referencia (DGI B606c código 1706)');
  }

  // ═══════════════════════════════════════════
  // VALIDACIONES DE ITEMS
  // ═══════════════════════════════════════════
  if (!wizardData.items || wizardData.items.length === 0) {
    errors.push('Debe agregar al menos un item a la factura');
  }

  if (wizardData.items && wizardData.items.length > 1000) {
    errors.push('No se pueden incluir más de 1,000 ítems por documento (DGI)');
  }

  // Validación por item (C10, C201, C202a, C203, C402a, C206)
  if (wizardData.items && wizardData.items.length > 0) {
    wizardData.items.forEach((item, index) => {
      const itemLabel = `Item ${index + 1} (${item.productName || item.productCode})`;

      // Nombre del producto requerido
      if (!item.productName?.trim()) {
        errors.push(`${itemLabel}: Nombre del producto es requerido`);
      }

      // Cantidad debe ser mayor a 0
      if (!item.quantity || item.quantity <= 0) {
        errors.push(`${itemLabel}: Cantidad debe ser mayor a 0`);
      }

      // DGI C10: Segmento CPBS obligatorio para receptor Gobierno (03)
      if (isGobierno && !item.segment?.trim()) {
        errors.push(`${itemLabel}: Segmento CPBS obligatorio para receptor Gobierno (DGI C10)`);
      }

      // DGI C201: Precio unitario > 100,000 → Notificación
      if (item.unitPrice > 100000) {
        warnings.push(`${itemLabel}: Precio unitario mayor a B/100,000 generará notificación DGI (C201)`);
      }

      // DGI C202a (2052): Descuento > 0 cuando precio = 0 → R
      if (item.unitDiscount > 0 && item.unitPrice === 0) {
        errors.push(`${itemLabel}: No puede tener descuento cuando el precio unitario es 0 (DGI C202a código 2052)`);
      }

      // DGI C203 (2053): subtotal ≠ quantity * (unitPrice - unitDiscount) → R
      const expectedSubtotal = round2(item.quantity * (item.unitPrice - item.unitDiscount));
      if (Math.abs(item.subtotal - expectedSubtotal) > 0.01) {
        errors.push(`${itemLabel}: Subtotal (${item.subtotal.toFixed(2)}) no coincide con cálculo esperado (${expectedSubtotal.toFixed(2)}) (DGI C203 código 2053)`);
      }

      // DGI C402a (2152): taxAmount ≠ subtotal * (taxRate/100) → R
      const expectedTax = round2(item.subtotal * (item.taxRate / 100));
      if (Math.abs(item.taxAmount - expectedTax) > 0.01) {
        errors.push(`${itemLabel}: ITBMS (${item.taxAmount.toFixed(2)}) no coincide con cálculo esperado (${expectedTax.toFixed(2)}) (DGI C402a código 2152)`);
      }

      // DGI C502a (2202): iscAmount ≠ subtotal * iscRate → R
      if (item.iscRate > 0) {
        const expectedIsc = round2(item.subtotal * item.iscRate);
        if (Math.abs(item.iscAmount - expectedIsc) > 0.01) {
          errors.push(`${itemLabel}: ISC (${item.iscAmount.toFixed(2)}) no coincide con cálculo esperado (${expectedIsc.toFixed(2)}) (DGI C502a código 2202)`);
        }
      }

      // DGI C601 (2300): Código OTI repetido en mismo ítem → R
      if (item.gOTIItem && item.gOTIItem.length > 1) {
        const otiCodes = item.gOTIItem.map(oti => oti.dCodOTI);
        const duplicateOti = otiCodes.filter((code, idx) => otiCodes.indexOf(code) !== idx);
        if (duplicateOti.length > 0) {
          errors.push(`${itemLabel}: Código OTI "${duplicateOti[0]}" repetido en el mismo ítem (DGI C601 código 2300)`);
        }
      }

      // DGI C206 (2056): total ≠ subtotal + shipping + insurance + tax + isc + oti → R
      const otiTotal = item.gOTIItem?.reduce((sum, oti) => sum + (parseFloat(oti.dValOTI) || 0), 0) || 0;
      const expectedTotal = round2(
        item.subtotal +
        (item.shippingAmount || 0) +
        (item.insuranceAmount || 0) +
        item.taxAmount +
        item.iscAmount +
        otiTotal
      );
      if (Math.abs(item.total - expectedTotal) > 0.01) {
        errors.push(`${itemLabel}: Total (${item.total.toFixed(2)}) no coincide con cálculo esperado (${expectedTotal.toFixed(2)}) (DGI C206 código 2056)`);
      }
    });

    // DGI C204/D07 (2054): No mezclar acarreo por ítem con acarreo total → R
    const hasItemShipping = wizardData.items.some(item => (item.shippingAmount || 0) > 0);
    if (hasItemShipping && (wizardData.shippingCost || 0) > 0) {
      errors.push('No se puede informar acarreo a nivel total (D07) si existe acarreo en algún ítem (C204) (DGI C204 código 2054)');
    }

    // DGI C205/D08 (2055): No mezclar seguro por ítem con seguro total → R
    const hasItemInsurance = wizardData.items.some(item => (item.insuranceAmount || 0) > 0);
    if (hasItemInsurance && (wizardData.insuranceCost || 0) > 0) {
      errors.push('No se puede informar seguro a nivel total (D08) si existe seguro en algún ítem (C205) (DGI C205 código 2055)');
    }
  }

  // ═══════════════════════════════════════════
  // TOTALES DE FACTURA (D09, D10, D11, D13, D14)
  // ═══════════════════════════════════════════
  if (wizardData.items && wizardData.items.length > 0) {
    const calcSubtotal = round2(wizardData.items.reduce((sum, item) => sum + item.subtotal, 0));
    const calcTotalTax = round2(wizardData.items.reduce((sum, item) => sum + item.taxAmount, 0));
    const calcTotalIsc = round2(wizardData.items.reduce((sum, item) => sum + item.iscAmount, 0));

    if (Math.abs(calcSubtotal - wizardData.subtotal) > 0.01) {
      errors.push(`Inconsistencia aritmética: subtotal declarado (${wizardData.subtotal.toFixed(2)}) no coincide con suma de ítems (${calcSubtotal.toFixed(2)}) (DGI D01-D10)`);
    }
    if (Math.abs(calcTotalTax - wizardData.totalTax) > 0.01) {
      errors.push(`Inconsistencia aritmética: ITBMS declarado (${wizardData.totalTax.toFixed(2)}) no coincide con suma de ítems (${calcTotalTax.toFixed(2)}) (DGI D01-D10)`);
    }
    if (Math.abs(calcTotalIsc - wizardData.totalIsc) > 0.01) {
      errors.push(`Inconsistencia aritmética: ISC declarado (${wizardData.totalIsc.toFixed(2)}) no coincide con suma de ítems (${calcTotalIsc.toFixed(2)}) (DGI D01-D10)`);
    }

    // DGI D09 (2507): grandTotal ≠ subtotal - discount + tax + isc + shipping + insurance + oti → R (tolerancia 0.50)
    const totalOti = wizardData.gOTITotal?.reduce((sum, oti) => sum + parseFloat(oti.dValOTITotal), 0) || 0;
    const totalDiscount = wizardData.globalDiscounts?.reduce((sum, d) => sum + d.amount, 0) || 0;
    const expectedGrandTotal = round2(
      wizardData.subtotal - totalDiscount + wizardData.totalTax + wizardData.totalIsc +
      (wizardData.shippingCost || 0) + (wizardData.insuranceCost || 0) + totalOti
    );
    if (Math.abs(wizardData.grandTotal - expectedGrandTotal) > 0.50) {
      errors.push(`Total general (${wizardData.grandTotal.toFixed(2)}) no coincide con cálculo esperado (${expectedGrandTotal.toFixed(2)}). Diferencia mayor a B/0.50 (DGI D09 código 2507)`);
    }

    // DGI D09b (2509): Total > 1 millón de dólares → Notificación (N)
    if (wizardData.grandTotal > 1000000) {
      warnings.push('El total de la factura supera B/1,000,000. La DGI enviará una notificación (DGI D09b código 2509)');
    }

    // DGI D09c (2515): Consumidor Final + total > 10 mil dólares → Notificación (N)
    if (wizardData.receiverType === '02' && wizardData.grandTotal > 10000) {
      warnings.push('Factura a Consumidor Final con total mayor a B/10,000. La DGI enviará una notificación (DGI D09c código 2515)');
    }

    // DGI D601/D602 (2800-2802): Totales OTI deben coincidir con suma de OTI por ítems → R
    if (wizardData.items.some(item => item.gOTIItem && item.gOTIItem.length > 0)) {
      // Calcular totales OTI por código desde los ítems
      const otiByCode: Record<string, number> = {};
      wizardData.items.forEach(item => {
        item.gOTIItem?.forEach(oti => {
          const code = oti.dCodOTI;
          otiByCode[code] = (otiByCode[code] || 0) + (parseFloat(oti.dValOTI) || 0);
        });
      });

      // Comparar con gOTITotal declarado
      const declaredOtiCodes = (wizardData.gOTITotal || []).map(oti => oti.dCodOTITotal);
      const calcOtiCodes = Object.keys(otiByCode);

      // D601: Verificar que todos los códigos de ítems estén en totales
      calcOtiCodes.forEach(code => {
        if (!declaredOtiCodes.includes(code)) {
          errors.push(`Código OTI "${code}" existe en ítems pero no en totales (DGI D601a código 2801)`);
        }
      });

      // D602: Verificar que los montos coincidan
      (wizardData.gOTITotal || []).forEach(otiTotal => {
        const expectedOtiAmount = round2(otiByCode[otiTotal.dCodOTITotal] || 0);
        const declaredAmount = parseFloat(otiTotal.dValOTITotal) || 0;
        if (Math.abs(declaredAmount - expectedOtiAmount) > 0.50) {
          errors.push(`Total OTI código "${otiTotal.dCodOTITotal}" (${declaredAmount.toFixed(2)}) no coincide con suma de ítems (${expectedOtiAmount.toFixed(2)}) (DGI D602 código 2802)`);
        }
      });
    }
  }

  // ═══════════════════════════════════════════
  // CONDICIÓN DE PAGO Y FORMAS DE PAGO (D12, D301, D401)
  // ═══════════════════════════════════════════

  // DGI D12 (2512): paymentCondition debe ser 1, 2, o 3 → R
  if (wizardData.paymentCondition && !['1', '2', '3'].includes(wizardData.paymentCondition)) {
    errors.push(`Condición de pago "${wizardData.paymentCondition}" no es válida. Debe ser 1 (Contado), 2 (Crédito) o 3 (Mixto) (DGI D12 código 2512)`);
  }

  // DGI D301 (2600): Cada forma de pago debe ser código válido → R
  if (wizardData.payments && wizardData.payments.length > 0) {
    wizardData.payments.forEach((payment, index) => {
      if (!VALID_PAYMENT_METHODS.includes(payment.method)) {
        errors.push(`Forma de pago #${index + 1} "${payment.method}" no es válida. Códigos válidos: ${VALID_PAYMENT_METHODS.join(', ')} (DGI D301 código 2600)`);
      }
      // DGI D302: Descripción obligatoria cuando forma de pago = 99 (Otro)
      if (payment.method === '99' && !payment.description?.trim()) {
        errors.push(`Forma de pago #${index + 1}: Descripción obligatoria cuando la forma de pago es "Otro" (99) (DGI D302)`);
      }
      // DGI D302a (2602): No informar descripción cuando forma de pago ≠ 99 → R
      if (payment.method !== '99' && payment.description?.trim()) {
        errors.push(`Forma de pago #${index + 1}: No se debe informar descripción cuando la forma de pago no es "Otro" (99) (DGI D302a código 2602)`);
      }
    });
  } else if (wizardData.grandTotal > 0) {
    errors.push('Debe agregar al menos una forma de pago');
  }

  // DGI D50: Pagos a plazo obligatorios cuando condición = Crédito (2) o Mixto (3)
  if (wizardData.paymentCondition && ['2', '3'].includes(wizardData.paymentCondition) && wizardData.grandTotal > 0) {
    if (!wizardData.paymentTerms || wizardData.paymentTerms.length === 0) {
      errors.push('Debe agregar al menos un pago a plazo cuando la condición es Crédito o Mixto (DGI D50)');
    } else {
      // DGI D504: Si dInfPagPlazo se informa, debe tener entre 15 y 1000 caracteres
      wizardData.paymentTerms.forEach((term, i) => {
        if (term.dInfPagPlazo && (term.dInfPagPlazo.length < 15 || term.dInfPagPlazo.length > 1000)) {
          errors.push(`La descripción del plazo #${i + 1} debe tener entre 15 y 1000 caracteres (DGI D504)`);
        }
      });

      // DGI D09a: Suma de pagos a plazo debe ser igual al total (tolerancia 0.50)
      const termsSum = round2(wizardData.paymentTerms.reduce((sum, term) => sum + (parseFloat(term.dValItPlazo) || 0), 0));
      if (Math.abs(termsSum - wizardData.grandTotal) > 0.50) {
        errors.push(`Suma de pagos a plazo (${termsSum.toFixed(2)}) no coincide con el total (${wizardData.grandTotal.toFixed(2)}). Diferencia mayor a B/0.50 (DGI D09a)`);
      }
    }
  }

  // Contado: suma de formas de pago debe cubrir el total
  if (wizardData.paymentCondition === '1' && wizardData.payments && wizardData.payments.length > 0) {
    const paymentSum = round2(wizardData.payments.reduce((sum, p) => sum + (parseFloat(String(p.amount)) || 0), 0));
    if (paymentSum < wizardData.grandTotal - 0.50) {
      errors.push(`Suma de formas de pago (${paymentSum.toFixed(2)}) no cubre el total (${wizardData.grandTotal.toFixed(2)}) para condición Contado`);
    }
  }

  // DGI D401 (2650): Código de retención debe ser válido → R
  if (wizardData.retention && wizardData.retention !== '0') {
    if (!VALID_RETENTION_CODES.includes(wizardData.retention)) {
      errors.push(`Código de retención "${wizardData.retention}" no es válido. Códigos válidos: ${VALID_RETENTION_CODES.join(', ')} (DGI D401 código 2650)`);
    }
  }

  // DGI D401: Gobierno debe tener retención
  if (isGobierno && (!wizardData.retention || wizardData.retention === '0')) {
    warnings.push('Receptor Gobierno generalmente requiere indicar código de retención (DGI D401)');
  }

  // DGI D402 (2651): Monto de retención ≠ ITBMS × tasa retención → Notificación (N)
  if (wizardData.retention && wizardData.retention !== '0' && wizardData.totalTax > 0) {
    if (wizardData.retention === '8') {
      // Código 8 (Otros): monto manual requerido
      if (!wizardData.retentionAmount || wizardData.retentionAmount <= 0) {
        errors.push('Retención código 8 (Otros) requiere ingresar el monto de retención manualmente (DGI D402)');
      }
    } else {
      const retentionRates: Record<string, number> = {
        '1': 1.00,   // 100% - Servicios profesionales al estado
        '2': 0.50,   // 50% - Bienes/servicios al estado
        '3': 1.00,   // 100% - No domiciliado
        '4': 0.50,   // 50% - Compra bienes/servicios
        '7': 0.50,   // 50% - Comercio afiliado TC/TD
      };
      const rate = retentionRates[wizardData.retention];
      if (rate !== undefined && rate > 0) {
        const expectedRetention = round2(wizardData.totalTax * rate);
        warnings.push(`Monto de retención calculado: B/${expectedRetention.toFixed(2)} (ITBMS B/${wizardData.totalTax.toFixed(2)} × ${(rate * 100).toFixed(0)}%) (DGI D402 código 2651)`);
      }
    }
  }

  // Validaciones de totales
  if (wizardData.grandTotal < 0) errors.push('El total de la factura no puede ser negativo');

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Convierte el payload de la API (formato DGI) a InvoiceWizardData
 * Esta es la función inversa de mapInvoiceToAPIFormat
 * @param payload - Payload de la factura desde la API (puede ser string o objeto)
 * @param invoiceMetadata - Metadatos adicionales de la factura (billNumber, cufe, createdDate, etc.)
 * @returns InvoiceWizardData para pre-llenar el wizard
 */
export const mapAPIFormatToWizard = (
  payload: any,
  invoiceMetadata: {
    billNumber?: string;
    cufe?: string;
    createdDate?: string;
    id?: string;
  }
): Partial<InvoiceWizardData> => {
  console.log('🔄 Iniciando mapeo inverso de API a Wizard...');

  // Validar que el payload existe
  if (!payload) {
    console.error('❌ Payload is null or undefined');
    throw new Error('El payload de la factura está vacío o es nulo');
  }

  // Si el payload es string, parsearlo
  let parsedPayload = payload;
  if (typeof payload === 'string') {
    try {
      parsedPayload = JSON.parse(payload);
      console.log('✅ Payload parseado correctamente');
    } catch (error) {
      console.error('❌ Error al parsear payload JSON:', error);
      throw new Error('Error al parsear el payload de la factura: JSON inválido');
    }
  }

  // Validar estructura mínima del payload
  if (!parsedPayload || typeof parsedPayload !== 'object') {
    console.error('❌ Payload no es un objeto válido:', parsedPayload);
    throw new Error('El payload de la factura no tiene una estructura válida');
  }

  console.log('✅ Payload válido, procediendo con mapeo...');

  // Extraer secciones principales del payload
  const dGen = parsedPayload?.dGen || {};
  const gEmis = dGen?.gEmis || {};
  const gDatRec = dGen?.gDatRec || {};
  const gTot = parsedPayload?.gTot || {}; // Totales están en gTot, NO gTotSub
  const gItem = parsedPayload?.gItem || [];
  const gFormaPago = gTot?.gFormaPago || []; // gFormaPago está dentro de gTot
  const gPagPlazo = gTot?.gPagPlazo || []; // Plazos de pago (crédito/mixto)
  const gRetenc = gTot?.gRetenc || {}; // Retención (gobierno)
  const gPedComGl = parsedPayload?.gPedComGl || {};
  const gFExp = dGen?.gFExp || {}; // Datos de exportación

  // Mapear items según la estructura real del payload
  const items: InvoiceItem[] = gItem.map((item: any, index: number) => {
    const gPrecios = item.gPrecios || {};
    const gITBMSItem = item.gITBMSItem || {};
    const gISCItem = item.gISCItem || {};
    const gOTIItem = item.gOTIItem || [];
    const gClasCom = item.gClasCom || {};
    const gPedComIr = item.gPedComIr || {};

    // Calcular valores del item
    const quantity = parseFloat(item.dCantCodInt) || 0;
    const unitPrice = parseFloat(gPrecios.dPrUnit) || 0;
    const unitDiscount = parseFloat(gPrecios.dPrUnitDesc) || 0;
    const taxAmount = parseFloat(gITBMSItem.dValITBMS) || 0;
    const iscAmount = parseFloat(gISCItem.dValISC) || 0;

    // Mapear el código de ITBMS a la tasa porcentual
    const taxCode = gITBMSItem.dTasaITBMS || '01';
    const taxRate = unmapITBMSRate(taxCode);

    // Mapear el código de ISC si existe (por ahora parseFloat como fallback)
    const iscRateValue = parseFloat(gISCItem.dTasaISC) || 0;

    // Subtotal = (Precio unitario - Descuento unitario) * Cantidad (SIN impuestos)
    // Usar round2() para evitar errores de punto flotante
    const itemSubtotal = round2((unitPrice - unitDiscount) * quantity);

    // Total del item = Subtotal + ISC + ITBMS + OTI (CON impuestos)
    const otiTotal = round2(gOTIItem.reduce((sum: number, oti: any) => sum + (parseFloat(oti.dValOTI) || 0), 0));
    const itemTotal = round2(itemSubtotal + iscAmount + taxAmount + otiTotal);

    return {
      id: `item-${index}-${Date.now()}`,
      productId: '',
      reference: item.dRefItem || '',
      productCode: item.dCodProd || '',
      productName: unescapeXml(item.dDescProd),
      description: '',
      quantity,
      unit: item.cUnidad || 'und',
      unitPrice,
      unitDiscount,
      discountPercent: 0,
      taxRate, // Usa la tasa mapeada desde el código (7, 10, 15, etc.)
      taxAmount, // Usa el valor que viene del payload
      iscRate: iscRateValue,
      iscAmount,
      otiAmount: 0, // Deprecated
      gOTIItem: gOTIItem.map((oti: any) => ({
        dCodOTI: oti.dCodOTI || '',
        dValOTI: oti.dValOTI || '0.00',
      })),
      shippingAmount: parseFloat(gPrecios.dPrAcarItem) || 0,
      insuranceAmount: parseFloat(gPrecios.dPrSegItem) || 0,
      // La API puede devolver CPBS en gClasCom (dSegCla/dFamCla) o en la raíz del item (dCodCPBSabr/dCodCPBScmp)
      segment: gClasCom.dSegCla || item.dCodCPBSabr || '',
      family: gClasCom.dFamCla || item.dCodCPBScmp || '',
      orderNumber: gPedComIr.dNroPed || '',
      orderItemSequence: gPedComIr.dSecItemPed || '',
      orderItemInfo: gPedComIr.dInfEmPedIt || '',
      subtotal: itemSubtotal, // Valor SIN impuestos
      total: itemTotal, // Valor CON impuestos
    };
  });

  // Mapear formas de pago (gFormaPago ya es un array directamente)
  const payments = (gFormaPago || []).map((payment: any) => ({
    method: payment.iFormaPago || '02', // 02 = Efectivo por defecto
    amount: parseFloat(payment.dVlrCuota) || 0,
    description: payment.dFormaPagoDesc || payment.PagoDesc || '',
  }));

  // Mapear descuentos globales
  // La API puede devolver como gGlobal o gDescBonif con diferentes nombres de campo
  const rawGlobalDiscounts = gTot.gGlobal || gTot.gDescBonif || [];
  const globalDiscounts = (Array.isArray(rawGlobalDiscounts) ? rawGlobalDiscounts : []).map((disc: any) => ({
    description: disc.dDescGloItem || disc.dDetalDesc || '',
    amount: parseFloat(disc.dVlrGloItem || disc.dValDesc) || 0,
  }));

  // Mapear OTI totales
  const gOTITotal = (gTot.gOTITotal || []).map((oti: any) => ({
    dCodOTITotal: oti.dCodOTITotal || '',
    dValOTITotal: oti.dValOTITotal || '0.00',
  }));

  // Mapear plazos de pago (gPagPlazo) - para crédito/mixto
  const paymentTerms = (Array.isArray(gPagPlazo) ? gPagPlazo : []).map((term: any) => ({
    dSecItem: term.dSecItem || '',
    dFecItPlazo: term.dFecItPlazo || '',
    dValItPlazo: term.dValItPlazo || '0.00',
    dInfPagPlazo: term.dInfPagPlazo || '',
  }));

  // Mapear retención (gRetenc) - obligatorio para Gobierno
  const retentionCode = gRetenc?.cCodRetenc || '0';
  const retentionAmount = parseFloat(gRetenc?.dValRetenc) || 0;

  // Construir InvoiceWizardData
  const wizardData: Partial<InvoiceWizardData> = {
    // Emisor
    issuerName: unescapeXml(gEmis.dNombEm),
    issuerCode: gEmis.gRucEmi?.dRuc || '',
    issuerDv: gEmis.gRucEmi?.dDV || '',
    issuerEmail: gEmis.dCorElectEm || '',
    issuerPhone: gEmis.dTfnEm || '',

    // Sucursal
    branchOfficeId: '', // Se debe buscar por branchOfficeAddressCode
    branchOfficeAddressCode: gEmis.dSucEm || '', // Código de la sucursal (ej: "0001")
    branchOfficeName: '', // Se llenará al seleccionar la sucursal
    branchOfficeAddress: unescapeXml(gEmis.dDirecEm),
    branchOfficeProvince: unescapeXml(gEmis.gUbiEm?.dProv),
    branchOfficeDistrict: unescapeXml(gEmis.gUbiEm?.dDistr),
    branchOfficeTownship: unescapeXml(gEmis.gUbiEm?.dCorreg),
    branchOfficeFiscalCode: gEmis.gUbiEm?.dCodUbi || '',
    branchOfficeCountryCode: 'PA',
    branchOfficeCountryName: 'PANAMÁ',
    billingPointCode: dGen.dPtoFacDF || '001',

    // Receptor
    receiverType: String(gDatRec.iTipoRec || '01'),
    customerName: unescapeXml(gDatRec.dNombRec),
    customerRuc: gDatRec.gRucRec?.dRuc || '',
    customerDv: String(gDatRec.gRucRec?.dDV || ''),
    customerRucType: (String(gDatRec.iTipoRec) === '04') ? '03' : '01', // Extranjero=Pasaporte, Nacional=RUC
    customerRecepType: String(gDatRec.gRucRec?.dTipoRuc || '1'),
    customerEmail: gDatRec.dCorElectRec || '',
    customerPhone: gDatRec.dTfnRec || '',

    // Cliente extranjero (si aplica) - gIdExt (NO gRucExt)
    customerForeignId: gDatRec.gIdExt?.dIdExt || '',
    customerForeignCountry: gDatRec.gIdExt?.dPaisExt || gDatRec.cPaisRec || '',

    // Dirección del receptor
    customerAddress: unescapeXml(gDatRec.dDirecRec),
    // cPaisRec es código ISO (ej: "PA"), customerCountry espera nombre (ej: "PANAMÁ")
    // Para el mapeo inverso, guardamos el código en customerForeignCountry y resolvemos el nombre
    customerCountry: (() => {
      const code = gDatRec.cPaisRec || '';
      // Mapa de códigos comunes a nombres para resolución inmediata
      const countryNames: Record<string, string> = {
        'PA': 'PANAMÁ', 'US': 'ESTADOS UNIDOS', 'CR': 'COSTA RICA', 'CO': 'COLOMBIA',
        'MX': 'MÉXICO', 'GT': 'GUATEMALA', 'NI': 'NICARAGUA', 'HN': 'HONDURAS',
        'SV': 'EL SALVADOR', 'EC': 'ECUADOR', 'PE': 'PERÚ', 'CL': 'CHILE',
        'BR': 'BRASIL', 'AR': 'ARGENTINA', 'VE': 'VENEZUELA', 'DO': 'REPÚBLICA DOMINICANA',
        'ES': 'ESPAÑA', 'CN': 'CHINA', 'JP': 'JAPÓN', 'KR': 'COREA DEL SUR',
      };
      return countryNames[code.toUpperCase()] || code;
    })(),
    customerProvince: unescapeXml(gDatRec.gUbiRec?.dProv),
    customerDistrict: unescapeXml(gDatRec.gUbiRec?.dDistr),
    customerTownship: unescapeXml(gDatRec.gUbiRec?.dCorreg),
    customerAddressFiscalCode: gDatRec.gUbiRec?.dCodUbi || '',
    customerCity: '',

    // Detalles de la factura
    invoiceNumber: '', // Se generará nuevo
    invoiceNumberMode: 'auto',
    invoiceDate: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
    dueDate: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
    invoiceType: 'FE',
    documentType: String(dGen.iDoc || '01'),

    // Referencia al documento original, para que corregir una nota rechazada no
    // la re-emita sin referencia. Se lee de `dGen.gDFRef`, donde la deja la API;
    // el nivel superior es el respaldo para los documentos que el portal emitió
    // mientras la mandaba en el lugar equivocado.
    ...(() => {
      const ref = dGen.gDFRef || parsedPayload?.gDFRef;
      if (!ref) return {};
      return {
        gDFRef: {
          // Adentro del portal el CUFE se guarda sin el prefijo `FE`.
          dCufeRef: cufeSinPrefijo(ref.dCufeRef) || null,
          dNroFacIE: ref.dNroFacIE || null,
          dNroFacPap: ref.dNroFacPap || null,
          dFechaDFRef: ref.dFechaDFRef || null,
        },
      };
    })(),
    operationType: String(dGen.iTipoOp || '1'),
    containerShipping: '1',
    operationDestination: String(dGen.iDest || '1'),
    operationNature: String(dGen.iNatOp || '01'),
    documentFormatGeneration: String(dGen.iFormCAFE || '1'),
    currency: 'USD',

    // Datos de exportación (gFExp) - solo cuando iDest=2
    ...(gFExp.cCondEntr || gFExp.cMoneda ? {
      exportIncoterms: gFExp.cCondEntr || '',
      exportCurrency: gFExp.cMoneda || '',
      exportCurrencyDescription: gFExp.cMonedaDesc || '',
      exportExchangeRate: parseFloat(gFExp.dCambio) || 0,
      exportPortOfEmbarkation: gFExp.dPuertoEmbarq || '',
    } : {}),

    // Items
    items,

    // Notas y referencia
    notes: unescapeXml(gPedComGl.dInfEmPedGl) || '',
    reference: invoiceMetadata.billNumber || '',
    retention: retentionCode,

    // Totales (gTot es la estructura correcta)
    subtotal: parseFloat(gTot.dTotNeto) || 0,
    totalTax: parseFloat(gTot.dTotITBMS) || 0,
    totalDiscount: parseFloat(gTot.dTotDesc) || 0,
    totalIsc: parseFloat(gTot.dTotISC) || 0,
    totalOti: parseFloat(gTot.dTotOTI) || 0,
    gOTITotal,
    shippingCost: parseFloat(gTot.dTotAcar) || 0,
    insuranceCost: parseFloat(gTot.dTotSeg) || 0,
    grandTotal: parseFloat(gTot.dVTot) || 0,

    // Descuentos globales
    globalDiscounts,

    // Información de pedido global
    globalOrderNumber: gPedComGl.dNroPed || '',
    globalOrderAcceptanceNumber: gPedComGl.dNumAcept || '',
    globalOrderReceiverCode: gPedComGl.dCodRec || '',
    globalOrderIssuerSystemCode: gPedComGl.dCodSisEm || '',
    globalOrderInfo: gPedComGl.dInfEmPedGl || '',

    // Tipo de transacción de venta
    salesTransactionType: String(dGen.iTipoTranVenta || '1'),

    // Condición de pago
    paymentCondition: String(gTot.iPzPag || '1'),

    // Formas de pago
    payments,

    // Plazos de pago (crédito/mixto)
    paymentTerms,

    // Retención (gobierno) — monto manual
    retentionAmount,

    // Legacy payment fields
    paymentMethod: '01',
    amountPaid: 0,
    change: 0,
    balance: 0,
  };

  console.log('✅ Mapeo inverso completado');
  return wizardData;
};
